import { ChecklistLead, LeadStats } from '@/types/leads.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, TrendingUp, Phone, Calendar, Users, CheckCircle, Target, XCircle } from 'lucide-react';
import { useMemo } from 'react';

interface LeadStatsCardsProps {
  leads: ChecklistLead[];
}

export const LeadStatsCards = ({ leads }: LeadStatsCardsProps) => {
  const stats: LeadStats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 2);

    const leadsToday = leads.filter(l => new Date(l.created_at) >= today).length;
    const leadsThisWeek = leads.filter(l => new Date(l.created_at) >= weekAgo).length;
    const leadsThisMonth = leads.filter(l => new Date(l.created_at) >= monthAgo).length;
    const leadsLastMonth = leads.filter(l => {
      const date = new Date(l.created_at);
      return date >= lastMonth && date < monthAgo;
    }).length;

    const newLeads = leads.filter(l => l.status === 'new').length;
    const contactedLeads = leads.filter(l => l.status === 'contacted').length;
    const qualifiedLeads = leads.filter(l => l.status === 'qualified').length;
    const convertedLeads = leads.filter(l => l.status === 'converted').length;
    const lostLeads = leads.filter(l => l.status === 'lost').length;

    const consultationRequests = leads.filter(l => l.wants_consultation).length;
    const conversionRate = leads.length > 0 ? (convertedLeads / leads.length) * 100 : 0;
    const monthOverMonthGrowth = leadsLastMonth > 0 
      ? ((leadsThisMonth - leadsLastMonth) / leadsLastMonth) * 100 
      : 0;

    return {
      totalLeads: leads.length,
      leadsToday,
      leadsThisWeek,
      leadsThisMonth,
      consultationRequests,
      conversionRate,
      monthOverMonthGrowth,
      newLeads,
      contactedLeads,
      qualifiedLeads,
      convertedLeads,
      lostLeads,
    };
  }, [leads]);

  const cards = [
    {
      title: 'Gesamt-Downloads',
      value: stats.totalLeads,
      subtitle: `${stats.monthOverMonthGrowth >= 0 ? '+' : ''}${stats.monthOverMonthGrowth.toFixed(1)}% zum Vormonat`,
      icon: Download,
      trend: stats.monthOverMonthGrowth,
    },
    {
      title: 'Neue Leads',
      value: stats.newLeads,
      subtitle: 'Noch nicht kontaktiert',
      icon: Users,
      trend: stats.newLeads,
    },
    {
      title: 'Kontaktiert',
      value: stats.contactedLeads,
      subtitle: 'In Bearbeitung',
      icon: Phone,
      trend: stats.contactedLeads,
    },
    {
      title: 'Qualifiziert',
      value: stats.qualifiedLeads,
      subtitle: 'Hohes Potenzial',
      icon: Target,
      trend: stats.qualifiedLeads,
    },
    {
      title: 'Konvertiert',
      value: stats.convertedLeads,
      subtitle: `${stats.conversionRate.toFixed(1)}% Conversion-Rate`,
      icon: CheckCircle,
      trend: stats.convertedLeads,
    },
    {
      title: 'Verloren',
      value: stats.lostLeads,
      subtitle: 'Nicht weiterverfolgt',
      icon: XCircle,
      trend: stats.lostLeads,
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className="text-xs text-muted-foreground mt-1">{card.subtitle}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
